import { combine, createStore, sample } from 'effector';
import { clearTokenFx, getMeFx, getMeWithSessionFx, loadTokenFx, refreshTokenFx, saveTokenFx } from './effects';
import {
    updateAuthSession,
    appStarted,
    getMeSucceeded,
    loginSucceeded,
    logoutSucceeded,
    refreshTokenSucceeded,
} from './events';
import { hasAdminRole } from './lib/roles';

export type AuthSession = {
    accessToken: string | null;
    userId: string | null;
    roles: string[];
    isAuthenticated: boolean;
    isInitialized: boolean;
    email: string | null;
};

const initialAuthSession: AuthSession = {
    accessToken: null,
    userId: null,
    roles: [],
    isAuthenticated: false,
    isInitialized: false,
    email: null,
};

export const $authSession = createStore<AuthSession>(initialAuthSession).on(updateAuthSession, (state, patch) => {
    const nextState: Partial<AuthSession> = {
        ...state,
        ...patch,
    };

    const { accessToken = null, userId = null, roles = [], isInitialized = false, email = null } = nextState;
    const isAuthenticated = Boolean(accessToken && userId && hasAdminRole(roles));

    return {
        accessToken,
        userId,
        roles,
        isAuthenticated,
        isInitialized,
        email,
    };
});

export const $accessToken = $authSession.map(({ accessToken }) => accessToken);
export const $isAuthenticated = $authSession.map(({ isAuthenticated }) => isAuthenticated);
export const $authRoles = $authSession.map(({ roles }) => roles);
export const $authUserId = $authSession.map(({ userId }) => userId);
export const $isAuthInitialized = $authSession.map(({ isInitialized }) => isInitialized);
export const $authEmail = $authSession.map(({ email }) => email);

export const $authInfo = combine({
    isAuthenticated: $isAuthenticated,
    roles: $authRoles,
    userId: $authUserId,
    email: $authEmail,
});

// Загрузка токена при старте приложения
sample({
    clock: appStarted,
    target: loadTokenFx,
});

// Вызов getMe после успешной загрузки токена
sample({
    clock: loadTokenFx.doneData,
    filter: (accessToken) => {
        return Boolean(accessToken);
    },
    target: getMeWithSessionFx,
});

// Обновление сессии при успешной загрузке токена
sample({
    clock: loadTokenFx.doneData,
    filter: (accessToken) => {
        return Boolean(accessToken);
    },
    fn: (accessToken) => {
        return {
            accessToken,
        };
    },
    target: updateAuthSession,
});

// Если токена нет, сразу завершаем инициализацию
sample({
    clock: loadTokenFx.doneData,
    filter: (accessToken) => {
        return !accessToken;
    },
    fn: () => {
        return { isInitialized: true };
    },
    target: updateAuthSession,
});

// Завершение инициализации после getMe
sample({
    clock: [getMeWithSessionFx.done, getMeWithSessionFx.fail, getMeFx.done, getMeFx.fail],
    fn: () => {
        return { isInitialized: true };
    },
    target: updateAuthSession,
});

sample({
    clock: [loginSucceeded],
    filter: (res) => {
        return hasAdminRole(res.roles);
    },
    fn: (res) => {
        const { accessToken, roles, userId } = res;

        return {
            accessToken,
            roles,
            userId,
        };
    },
    target: updateAuthSession,
});

// Сохранение токена в storage при успешной аутентификации
sample({
    clock: [loginSucceeded],
    filter: (res) => {
        return Boolean(res?.accessToken && hasAdminRole(res.roles));
    },
    fn: (res) => {
        return res.accessToken as string;
    },
    target: saveTokenFx,
});

// Сохранение токена при refresh
sample({
    clock: refreshTokenSucceeded,
    filter: (result) => Boolean(result?.accessToken),
    fn: (result) => result.accessToken,
    target: saveTokenFx,
});

sample({
    clock: saveTokenFx.done,
    target: getMeWithSessionFx,
});

sample({
    clock: [refreshTokenSucceeded, refreshTokenFx.doneData],
    fn: (result) => {
        return {
            accessToken: result.accessToken ?? null,
        };
    },
    target: updateAuthSession,
});

sample({
    clock: [getMeSucceeded, getMeFx.doneData],
    fn: (result) => {
        return {
            roles: result.roles,
            userId: result.user_id,
            email: result.info?.email,
        };
    },
    target: updateAuthSession,
});

sample({
    clock: logoutSucceeded,
    target: clearTokenFx,
});

// Обновить сессию при logout, сохраняя isInitialized
sample({
    clock: logoutSucceeded,
    fn: () => {
        return {
            accessToken: null,
            userId: null,
            roles: [],
            isAuthenticated: false,
            isInitialized: true,
            email: null,
        };
    },
    target: updateAuthSession,
});
